import { useEffect, useRef, useState } from 'react';
import { createChart, CandlestickSeries, HistogramSeries, LineSeries, LineStyle } from 'lightweight-charts';
import { getValidToken } from '../utils/auth';
import { TIMEFRAMES, SESSION_TF, baseChartOptions, DayLinePrimitive, buildDayLines, SessionBandPrimitive, buildSessionBands, SessionLegend, SessionSelector, filterBySession, sessionAllows, attachVerticalPan, attachMeasureTool } from '../utils/chartUtils';
import { sma, rsiWilder, macd, vwapSession, toLineData, toHistData } from '../utils/indicators';

const API_URL = process.env.REACT_APP_API_URL || '';

const POLL_MS = {
  '1m': 30000, '5m': 60000, '15m': 120000,
  '30m': 180000, '1h': 300000, '4h': 600000, '1D': 900000,
};

// Indicator settings (user-specified, not the library defaults).
const RSI_PERIOD = 7;
const RSI_SMOOTH_PERIOD = 3; // SMA of the RSI line itself
const RSI_BANDS = [80, 50, 20];
const MACD_FAST = 5;
const MACD_SLOW = 13;
const MACD_SIGNAL = 2;
const VOL_MA_PERIOD = 20;

const UP_COLOR = '#26a69a';
const DOWN_COLOR = '#ef5350';

// Pane heights (pane 0 = candles, 1 = volume, 2 = RSI, 3 = MACD).
const CHART_PANE_HEIGHT = 300;
const VOL_PANE_HEIGHT = 80;
const RSI_PANE_HEIGHT = 110;
const MACD_PANE_HEIGHT = 110;
const TOTAL_HEIGHT = CHART_PANE_HEIGHT + VOL_PANE_HEIGHT + RSI_PANE_HEIGHT + MACD_PANE_HEIGHT;

// Parse OCC symbol → human-readable parts.
// "SPXW260629C07520000" → { underlying:"SPXW", expiry:"06/29/26", type:"CALL", strike:7520 }
function parseContractSymbol(symbol) {
  if (!symbol) return null;
  const m = symbol.match(/^([A-Z.]+)(\d{6})([CP])(\d{8})$/);
  if (!m) return null;
  const [, underlying, yymmdd, cp, strikeRaw] = m;
  const yy = yymmdd.slice(0, 2), mm = yymmdd.slice(2, 4), dd = yymmdd.slice(4, 6);
  return {
    underlying,
    expiry:  `${mm}/${dd}/20${yy}`,
    type:    cp === 'C' ? 'CALL' : 'PUT',
    strike:  parseInt(strikeRaw, 10) / 1000,
  };
}

// How many seconds each timeframe bar covers — used to compute the current
// bar's start time for the synthetic live-price candle.
const TF_SECONDS = {
  '1m': 60, '5m': 300, '15m': 900, '30m': 1800,
  '1h': 3600, '4h': 14400, '1D': 86400,
};

export default function ContractChartWidget({ contract }) {
  const [tf, setTf] = useState('1m');
  const [session, setSession] = useState('global');
  const [measureOn, setMeasureOn] = useState(false);

  const containerRef  = useRef(null);
  const chartRef      = useRef(null);
  const seriesRef     = useRef(null);
  const primitiveRef  = useRef(null);
  const dayLineRef    = useRef(null);
  const pollRef       = useRef(null);
  const mountedRef   = useRef(true);
  const initialDone    = useRef(false);
  const liveBarRef     = useRef(null);
  const resetVPanRef   = useRef(null);
  const latestPriceRef = useRef(null);
  const measureToolRef = useRef(null);

  // Indicator sub-pane series (RSI/MACD/Volume/VWAP) — created once on mount.
  const vwapSeriesRef       = useRef(null);
  const volSeriesRef        = useRef(null);
  const volMaSeriesRef      = useRef(null);
  const rsiSeriesRef        = useRef(null);
  const rsiSmoothSeriesRef  = useRef(null);
  const macdLineSeriesRef   = useRef(null);
  const macdSignalSeriesRef = useRef(null);
  const macdHistSeriesRef   = useRef(null);

  // ── Create chart once on mount ─────────────────────────────────────────────
  // The container div is ALWAYS rendered (never display:none) so that
  // clientWidth is a real pixel value when createChart() is called.
  // A ResizeObserver handles any layout changes, including the widget going
  // from 0→full width on first paint in some browser/layout combinations.
  useEffect(() => {
    mountedRef.current = true;

    const opts = baseChartOptions(containerRef.current.clientWidth || 300);
    opts.height = TOTAL_HEIGHT;
    const chart = createChart(containerRef.current, opts);
    chartRef.current = chart;

    const series = chart.addSeries(CandlestickSeries, {
      upColor: '#26a69a', downColor: '#ef5350',
      borderUpColor: '#26a69a', borderDownColor: '#ef5350',
      wickUpColor: '#26a69a', wickDownColor: '#ef5350',
    });
    seriesRef.current = series;

    const primitive = new SessionBandPrimitive();
    primitiveRef.current = primitive;
    series.attachPrimitive(primitive);

    const dayLine = new DayLinePrimitive();
    dayLineRef.current = dayLine;
    series.attachPrimitive(dayLine);

    // ── VWAP overlay — same pane as the candles, session-anchored, blue line
    // matching the user's TradingView VWAP style (Anchor=Session,
    // Source=(H+L+C)/3, no bands). ─────────────────────────────────────────
    const vwapSeries = chart.addSeries(LineSeries, {
      color: '#2563eb', lineWidth: 1, priceScaleId: 'right',
      title: 'VWAP', priceLineVisible: false, lastValueVisible: true,
    }, 0);
    vwapSeriesRef.current = vwapSeries;

    // ── Volume pane (1) — histogram colored by each bar's own O/C, plus an
    // SMA(20) overlay line. ─────────────────────────────────────────────────
    const volSeries = chart.addSeries(HistogramSeries, {
      priceFormat: { type: 'volume' },
      priceScaleId: 'right',
      title: 'Volume',
    }, 1);
    volSeriesRef.current = volSeries;
    const volMaSeries = chart.addSeries(LineSeries, {
      color: '#f59e0b', lineWidth: 1, priceScaleId: 'right',
      title: `Vol MA(${VOL_MA_PERIOD})`, priceLineVisible: false, lastValueVisible: false,
    }, 1);
    volMaSeriesRef.current = volMaSeries;

    // ── RSI pane (2) — RSI(9) line + SMA(9) smoothing line + 80/50/20 bands. ─
    const rsiSeries = chart.addSeries(LineSeries, {
      color: '#a78bfa', lineWidth: 1, priceScaleId: 'right',
      title: `RSI(${RSI_PERIOD})`, autoscaleInfoProvider: () => ({ priceRange: { minValue: 0, maxValue: 100 } }),
    }, 2);
    rsiSeriesRef.current = rsiSeries;
    const rsiSmoothSeries = chart.addSeries(LineSeries, {
      color: '#f59e0b', lineWidth: 1, priceScaleId: 'right',
      title: `SMA(${RSI_SMOOTH_PERIOD})`, priceLineVisible: false, lastValueVisible: false,
    }, 2);
    rsiSmoothSeriesRef.current = rsiSmoothSeries;
    const [rsiUpper, rsiMiddle, rsiLower] = RSI_BANDS;
    rsiSeries.createPriceLine({ price: rsiUpper, color: '#ef4444', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: 'Upper' });
    rsiSeries.createPriceLine({ price: rsiMiddle, color: '#6b7280', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: 'Mid' });
    rsiSeries.createPriceLine({ price: rsiLower, color: '#22c55e', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: 'Lower' });

    // ── MACD pane (3) — MACD/signal lines + histogram. ───────────────────────
    const macdLineSeries = chart.addSeries(LineSeries, {
      color: '#3b82f6', lineWidth: 1, priceScaleId: 'right',
      title: `MACD(${MACD_FAST},${MACD_SLOW},${MACD_SIGNAL})`,
    }, 3);
    macdLineSeriesRef.current = macdLineSeries;
    const macdSignalSeries = chart.addSeries(LineSeries, {
      color: '#f59e0b', lineWidth: 1, priceScaleId: 'right',
      title: 'Signal', priceLineVisible: false, lastValueVisible: false,
    }, 3);
    macdSignalSeriesRef.current = macdSignalSeries;
    const macdHistSeries = chart.addSeries(HistogramSeries, {
      priceScaleId: 'right', title: 'Hist', priceLineVisible: false, lastValueVisible: false,
    }, 3);
    macdHistSeriesRef.current = macdHistSeries;

    const panes = chart.panes();
    panes[0]?.setHeight(CHART_PANE_HEIGHT);
    panes[1]?.setHeight(VOL_PANE_HEIGHT);
    panes[2]?.setHeight(RSI_PANE_HEIGHT);
    panes[3]?.setHeight(MACD_PANE_HEIGHT);

    // ResizeObserver fires whenever the container's size changes — this
    // handles both window resize AND the first layout paint on desktop
    // where clientWidth may briefly be 0 during SSR / flex layout passes.
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width;
      if (w > 0 && mountedRef.current && chartRef.current) {
        chartRef.current.applyOptions({ width: w });
      }
    });
    ro.observe(containerRef.current);

    const { detach: detachVPan, reset } = attachVerticalPan(containerRef.current, [series]);
    resetVPanRef.current = reset;

    measureToolRef.current = attachMeasureTool(chart, series, containerRef.current);

    return () => {
      mountedRef.current = false;
      detachVPan();
      resetVPanRef.current = null;
      measureToolRef.current?.detach();
      measureToolRef.current = null;
      ro.disconnect();
      clearInterval(pollRef.current);
      chart.remove();
      chartRef.current = seriesRef.current = primitiveRef.current = dayLineRef.current = null;
      vwapSeriesRef.current = null;
      volSeriesRef.current = volMaSeriesRef.current = null;
      rsiSeriesRef.current = rsiSmoothSeriesRef.current = null;
      macdLineSeriesRef.current = macdSignalSeriesRef.current = macdHistSeriesRef.current = null;
    };
  }, []);

  // Toggle the measure tool's drag gesture on/off with the header button.
  useEffect(() => {
    measureToolRef.current?.setActive(measureOn);
  }, [measureOn]);

  // Push the live contract price as a synthetic partial bar onto the chart.
  // Tracks O/H/L within the current bar interval so the candle is meaningful.
  // Safe to call before initialDone — returns early if chart isn't ready.
  const applyLivePrice = (price) => {
    if (!seriesRef.current || !price || !initialDone.current) return;
    const nowSec = Math.floor(Date.now() / 1000);
    if (SESSION_TF.has(tf) && !sessionAllows(session, nowSec)) return;
    const barSec = TF_SECONDS[tf] || 60;
    const barStart = Math.floor(nowSec / barSec) * barSec;
    const prev = liveBarRef.current;
    const bar = {
      time:  barStart,
      open:  (prev?.time === barStart) ? prev.open              : price,
      high:  (prev?.time === barStart) ? Math.max(prev.high, price) : price,
      low:   (prev?.time === barStart) ? Math.min(prev.low,  price) : price,
      close: price,
    };
    liveBarRef.current = bar;
    try { seriesRef.current.update(bar); } catch { /* chart not ready */ }
  };

  // Recompute RSI/MACD/Volume from the full completed-bar history and push
  // to their sub-pane series. Runs on every bars fetch (initial + poll) —
  // these are lagging indicators computed off closed bars, not the live
  // forming candle, so tick-level recompute isn't needed here.
  const updateIndicators = (bars) => {
    if (!bars || !bars.length) return;
    const closes = bars.map(b => b.close);
    const volumes = bars.map(b => b.volume || 0);

    const rsiVals = rsiWilder(closes, RSI_PERIOD);
    const rsiFirstValid = rsiVals.findIndex(v => v != null);
    const rsiSmoothVals = new Array(closes.length).fill(null);
    if (rsiFirstValid !== -1) {
      const smoothTail = sma(rsiVals.slice(rsiFirstValid), RSI_SMOOTH_PERIOD);
      for (let i = 0; i < smoothTail.length; i++) rsiSmoothVals[rsiFirstValid + i] = smoothTail[i];
    }

    const { macdLine, signalLine, hist } = macd(closes, MACD_FAST, MACD_SLOW, MACD_SIGNAL);
    const volMaVals = sma(volumes, VOL_MA_PERIOD);

    volSeriesRef.current?.setData(toHistData(bars, volumes, b => (b.close >= b.open ? UP_COLOR : DOWN_COLOR)));
    volMaSeriesRef.current?.setData(toLineData(bars, volMaVals));
    rsiSeriesRef.current?.setData(toLineData(bars, rsiVals));
    rsiSmoothSeriesRef.current?.setData(toLineData(bars, rsiSmoothVals));
    macdLineSeriesRef.current?.setData(toLineData(bars, macdLine));
    macdSignalSeriesRef.current?.setData(toLineData(bars, signalLine));
    macdHistSeriesRef.current?.setData(toHistData(bars, hist, (b, v) => (v >= 0 ? UP_COLOR : DOWN_COLOR)));

    vwapSeriesRef.current?.setData(toLineData(bars, vwapSession(bars)));
  };

  // ── Refetch when contract symbol or timeframe changes ─────────────────────
  useEffect(() => {
    if (!contract?.symbol) return;

    initialDone.current = false;
    liveBarRef.current = null;
    latestPriceRef.current = contract.price ?? null;
    clearInterval(pollRef.current);
    resetVPanRef.current?.();
    seriesRef.current?.setData([]);
    primitiveRef.current?.clearBands();
    dayLineRef.current?.clearLines();
    vwapSeriesRef.current?.setData([]);
    volSeriesRef.current?.setData([]);
    volMaSeriesRef.current?.setData([]);
    rsiSeriesRef.current?.setData([]);
    rsiSmoothSeriesRef.current?.setData([]);
    macdLineSeriesRef.current?.setData([]);
    macdSignalSeriesRef.current?.setData([]);
    macdHistSeriesRef.current?.setData([]);

    const fetchBars = async (isInitial) => {
      if (!mountedRef.current || !seriesRef.current) return;
      try {
        const token = await getValidToken();
        if (!token || !mountedRef.current) return;

        const res = await fetch(
          `${API_URL}/api/chart/contract-bars?symbol=${encodeURIComponent(contract.symbol)}&tf=${tf}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok || !mountedRef.current) return;

        const bars = await res.json();
        if (!mountedRef.current) return;

        // Session filtering only applies to intraday timeframes — a 4h/1D bar
        // spans multiple sessions, so filtering by time-of-day would wrongly
        // drop it.
        const effectiveSession = SESSION_TF.has(tf) ? session : 'global';
        const visibleBars = filterBySession(bars, effectiveSession);

        if (isInitial || !initialDone.current) {
          resetVPanRef.current?.();
          seriesRef.current.setData(visibleBars || []);
          chartRef.current.priceScale('right').applyOptions({ autoScale: true });
          if (visibleBars && visibleBars.length) {
            chartRef.current.timeScale().fitContent();
          }
          initialDone.current = true;
        } else if (visibleBars && visibleBars.length) {
          seriesRef.current.update(visibleBars[visibleBars.length - 1]);
        }

        updateIndicators(visibleBars);

        // Day separator lines — drawn across all historical bars.
        // Session bands — only today's bars so pre-market doesn't appear twice
        // on multi-day charts (yesterday's data sits on the plain dark background).
        if (visibleBars && visibleBars.length) {
          dayLineRef.current?.setLines(buildDayLines(visibleBars));
          if (SESSION_TF.has(tf)) {
            const todayET = new Date().toLocaleDateString('en-US', { timeZone: 'America/New_York' });
            const todayBars = visibleBars.filter(b =>
              new Date(b.time * 1000).toLocaleDateString('en-US', { timeZone: 'America/New_York' }) === todayET
            );
            primitiveRef.current?.setBands(buildSessionBands(todayBars, { fitContent: true }));
          } else {
            primitiveRef.current?.clearBands();
          }
        }

        // Always re-apply the live price on top of the fetched bars so the
        // current partial bar is visible (bars API only returns completed bars).
        if (contract?.price) {
          latestPriceRef.current = contract.price;
          applyLivePrice(contract.price);
        }
      } catch { /* keep stale view */ }
    };

    fetchBars(true);
    pollRef.current = setInterval(() => fetchBars(false), POLL_MS[tf] || 30000);
    return () => clearInterval(pollRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract?.symbol, tf, session]);

  // ── Update live bar whenever the widget's live price changes ──────────────
  useEffect(() => {
    if (contract?.price) {
      latestPriceRef.current = contract.price;
      applyLivePrice(contract.price);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract?.price]);

  // ── Keep the forming candle rolling over bar boundaries on time, not just
  // when the price happens to change ────────────────────────────────────────
  // contract.price only updates when a poll lands (every few seconds) AND the
  // price actually moved. Without this, a new bar period (e.g. the top of a
  // new minute) wouldn't get its own candle until the next price change —
  // this ticks every second so a fresh candle appears right at the boundary
  // and the current one keeps "closing" live, TradingView-style.
  useEffect(() => {
    const id = setInterval(() => {
      if (latestPriceRef.current != null) applyLivePrice(latestPriceRef.current);
    }, 1000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tf, session]);

  const parsed = contract ? parseContractSymbol(contract.symbol) : null;
  const isCall  = parsed?.type === 'CALL';
  const typeColor = isCall ? '#22c55e' : '#ef4444';

  return (
    <div className="bg-gray-900 rounded-2xl p-5 mb-5">

      {/* Header */}
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        {parsed ? (
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold px-3 py-1.5 rounded border"
              style={{ backgroundColor: '#111827', color: '#e5e7eb', borderColor: '#374151' }}>
              {parsed.underlying}
            </span>
            <span className="text-xs font-semibold px-2 py-1 rounded"
              style={{ backgroundColor: typeColor + '22', color: typeColor }}>
              {parsed.type}
            </span>
            <span className="text-xs font-mono" style={{ color: '#60a5fa' }}>
              ${parsed.strike % 1 === 0 ? parsed.strike.toFixed(0) : parsed.strike.toFixed(1)}
            </span>
            <span className="text-xs text-gray-500">{parsed.expiry}</span>
            {/* Live contract price - same blue as the options table's underlying
                price, and same live data (contract.price is tick-driven through
                GenericOptionsWidget's WS overlay, not a separate fetch here) */}
            {contract?.price != null && (
              <span className="text-sm font-bold tabular-nums" style={{ color: '#60a5fa' }}>
                ${contract.price.toFixed(2)}
              </span>
            )}
          </div>
        ) : (
          <span className="text-xs text-gray-500">No contract selected</span>
        )}

        <div className="flex-1" />

        {/* Independent timeframe buttons */}
        <div className="flex items-center gap-1">
          {TIMEFRAMES.map(t => (
            <button
              key={t}
              onClick={() => setTf(t)}
              className="text-xs font-medium px-2 py-1 rounded transition-colors"
              style={
                t === tf
                  ? { backgroundColor: '#60a5fa22', color: '#60a5fa', border: '1px solid #60a5fa55' }
                  : { backgroundColor: 'transparent', color: '#6b7280', border: '1px solid transparent' }
              }
            >{t}</button>
          ))}
        </div>

        <button
          onClick={() => setMeasureOn(v => !v)}
          title="Drag on the chart to measure price/time between two points"
          className="text-xs font-medium px-2 py-1 rounded transition-colors"
          style={
            measureOn
              ? { backgroundColor: '#60a5fa22', color: '#60a5fa', border: '1px solid #60a5fa55' }
              : { backgroundColor: 'transparent', color: '#6b7280', border: '1px solid #374151' }
          }
        >📏 Measure</button>

        {SESSION_TF.has(tf) && <SessionSelector session={session} setSession={setSession} />}
        {SESSION_TF.has(tf) && <SessionLegend />}

      </div>

      {/* Chart container — always in the DOM so clientWidth is always valid.
          A placeholder message is overlaid when no contract is selected.    */}
      <div className="relative" style={{ height: TOTAL_HEIGHT }}>
        <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
        {!contract?.symbol && (
          <div className="absolute inset-0 flex items-center justify-center"
            style={{ color: '#374151', fontSize: 13, pointerEvents: 'none' }}>
            Select a contract below to view its chart
          </div>
        )}
      </div>
    </div>
  );
}
